<template>
  <b-card class="mb-4">
    <template #header>
      <div class="d-flex justify-content-between align-items-center">
        <h5 class="mb-0">Tarjetas / Códigos emitidos</h5>
        <div>
          <download-csv
            v-if="cards.length > 0"
            :data="cards"
            :name="`codigos_${program.name || program.id}.csv`"
            separator-excel
            class="btn btn-outline-secondary btn-sm mr-2"
          >
            <i class="fas fa-download mr-1"></i> Exportar CSV
          </download-csv>
          <b-button variant="primary" size="sm" @click="openCreateModal">
            <i class="fas fa-plus mr-1"></i> Crear códigos
          </b-button>
        </div>
      </div>
    </template>

    <p class="text-muted small mb-3">
      Las tarjetas son códigos únicos por cliente con su propio saldo de puntos.
      Úsalas para vales recargables o membresías individuales. Si solo necesitas un
      código compartido para todos (ej. <code>BANORTE10</code>), no hace falta crear tarjetas:
      basta con la regla del programa.
    </p>

    <b-row class="mb-2">
      <b-col md="6">
        <b-form-input
          type="search"
          v-model="search"
          placeholder="Buscar por código, cliente, teléfono..."
          debounce="300"
          @input="page = 1"
        ></b-form-input>
      </b-col>
    </b-row>

    <b-table
      :filter="search"
      small
      stacked="sm"
      striped
      hover
      :items="cards"
      :fields="cardFields"
      :busy="loading"
      :per-page="byPage"
      :current-page="page"
      responsive
      show-empty
      empty-text="No hay tarjetas emitidas aún."
      empty-filtered-text="No se encontraron tarjetas con esa búsqueda."
    >
      <template #table-busy>
        <div class="text-center text-primary my-3">
          <b-spinner small></b-spinner> Cargando...
        </div>
      </template>
      <template #cell(points)="data">
        <b-badge variant="info" pill>{{ data.item.points }}</b-badge>
      </template>
      <template #cell(expiration_date)="data">
        {{ formatDate(data.item.expiration_date) }}
      </template>
      <template #cell(actions)="data">
        <b-button size="sm" variant="outline-info" class="mr-1" @click="viewConsumption(data.item.code)">
          <i class="fas fa-history"></i>
        </b-button>
        <b-button size="sm" variant="outline-primary" @click="editCard(data.item.id)">
          Editar / Recargar
        </b-button>
      </template>
    </b-table>

    <b-pagination
      v-if="cards.length > byPage"
      v-model="page"
      :total-rows="cards.length"
      :per-page="byPage"
      align="center"
      size="sm"
    ></b-pagination>

    <!-- Modal: Crear códigos en lote -->
    <b-modal id="modal-create-cards" v-model="createModal.show" title="Crear códigos" hide-footer size="lg">
      <div v-if="createModal.createdCards.length === 0">
        <p class="text-muted">Genera uno o varios códigos con saldo inicial.</p>
        <b-row>
          <b-col md="6">
            <b-form-group label="Cantidad de códigos a generar:">
              <b-form-input v-model.number="createModal.quantity" type="number" min="1" step="1"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Puntos iniciales por código:">
              <b-form-input v-model.number="createModal.points" type="number" min="0" step="1"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Fecha de expiración (opcional):">
              <b-form-input type="datetime-local" v-model="createModal.expiration_date"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col v-if="createModal.quantity === 1" md="6">
            <b-form-group label="Código personalizado (opcional):">
              <b-form-input v-model="createModal.code" placeholder="Si lo dejas vacío se genera automático"></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>
        <div class="d-flex justify-content-end mt-3">
          <b-button variant="secondary" class="mr-2" @click="createModal.show = false">Cancelar</b-button>
          <b-button variant="success" :disabled="createModal.submitting" @click="postCreateCards">
            <b-spinner v-if="createModal.submitting" small></b-spinner>
            Crear
          </b-button>
        </div>
      </div>

      <div v-else>
        <p class="text-success">
          <i class="fas fa-check-circle"></i>
          Se crearon {{ createModal.createdCards.length }} código(s) correctamente.
        </p>
        <download-csv
          :data="createModal.createdCards"
          :name="`codigos_nuevos_${program.name || program.id}.csv`"
          separator-excel
          class="btn btn-outline-primary btn-sm mb-2"
        >
          <i class="fas fa-download mr-1"></i> Descargar lote recién creado
        </download-csv>
        <b-table
          stacked="sm"
          striped
          hover
          small
          :items="createModal.createdCards"
          :fields="newCardFields"
          responsive
        ></b-table>
        <div class="d-flex justify-content-end">
          <b-button variant="primary" @click="closeCreateModal">Listo</b-button>
        </div>
      </div>
    </b-modal>

    <!-- Modal: Editar / Recargar tarjeta -->
    <b-modal v-model="editModal.show" :title="`Tarjeta ${editModal.card.code || ''}`" hide-footer size="lg">
      <b-row>
        <b-col md="6">
          <b-form-group label="Código">
            <b-form-input :value="editModal.card.code" disabled></b-form-input>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Saldo actual de puntos">
            <b-form-input :value="editModal.card.points" disabled></b-form-input>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Nombre del cliente">
            <b-form-input v-model="editModal.card.customer_name"></b-form-input>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Teléfono">
            <b-form-input v-model="editModal.card.customer_phone"></b-form-input>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Email">
            <b-form-input v-model="editModal.card.customer_email"></b-form-input>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Fecha de expiración">
            <b-form-input type="datetime-local" v-model="editModal.card.expiration_date"></b-form-input>
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group label="Fecha de creación">
            <b-form-input :value="editModal.card.create_date" disabled></b-form-input>
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group label="Creado por">
            <b-form-input :value="editModal.card.created_by_user_name || '—'" disabled></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>

      <div class="d-flex justify-content-end mb-3">
        <b-button variant="secondary" class="mr-2" @click="editModal.show = false">Cancelar</b-button>
        <b-button variant="success" :disabled="editModal.savingData" @click="saveCardData">
          <b-spinner v-if="editModal.savingData" small></b-spinner>
          Guardar datos
        </b-button>
      </div>

      <hr />

      <h6 class="mt-2">Recargar / Descontar saldo</h6>
      <b-row class="align-items-end">
        <b-col md="5">
          <b-form-group label="Sumar puntos">
            <b-input-group>
              <b-form-input v-model.number="editModal.addPoints" type="number" min="1" step="1"></b-form-input>
              <b-input-group-append>
                <b-button variant="success" :disabled="editModal.movingPoints || !(editModal.addPoints > 0)" @click="addPointsServer">
                  <i class="fas fa-plus"></i>
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col md="5">
          <b-form-group label="Restar puntos">
            <b-input-group>
              <b-form-input v-model.number="editModal.subtractPoints" type="number" min="1" step="1"></b-form-input>
              <b-input-group-append>
                <b-button
                  variant="danger"
                  :disabled="editModal.movingPoints || !(editModal.subtractPoints > 0) || editModal.subtractPoints > editModal.card.points"
                  @click="subtractPointsServer"
                >
                  <i class="fas fa-minus"></i>
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col md="2">
          <small class="text-muted">Total tras movimiento:</small>
          <h5 class="mb-0">{{ editModal.card.points + (editModal.addPoints || 0) - (editModal.subtractPoints || 0) }}</h5>
        </b-col>
      </b-row>
    </b-modal>

    <!-- Modal: Historial de consumo -->
    <b-modal v-model="consumptionModal.show" title="Historial de consumo" hide-footer size="lg">
      <b-table
        v-if="consumptionModal.items.length > 0"
        stacked="sm"
        striped
        hover
        small
        :items="consumptionModal.items"
        responsive
      ></b-table>
      <p v-else class="text-muted text-center py-3">Esta tarjeta no tiene movimientos registrados.</p>
    </b-modal>
  </b-card>
</template>

<script>
import axios from "axios";

const blankCreateModal = () => ({
  show: false,
  submitting: false,
  quantity: 1,
  points: 0,
  expiration_date: "",
  code: "",
  createdCards: []
});

const blankEditModal = () => ({
  show: false,
  savingData: false,
  movingPoints: false,
  addPoints: null,
  subtractPoints: null,
  card: {
    id: null,
    code: "",
    points: 0,
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    expiration_date: "",
    create_date: "",
    created_by_user_name: ""
  }
});

export default {
  name: "LoyaltyProgramCards",
  props: {
    program: { type: Object, required: true }
  },
  data() {
    return {
      cards: [],
      loading: false,
      search: "",
      page: 1,
      byPage: 10,
      cardFields: [
        { key: "code", label: "Código", sortable: true },
        { key: "customer_name", label: "Cliente", sortable: true },
        { key: "customer_phone", label: "Teléfono" },
        { key: "points", label: "Puntos", sortable: true, class: "text-center" },
        { key: "expiration_date", label: "Expira", sortable: true },
        { key: "actions", label: "Acciones", class: "text-right" }
      ],
      newCardFields: [
        { key: "code", label: "Código" },
        { key: "points", label: "Puntos" },
        { key: "expiration_date", label: "Expira" }
      ],
      createModal: blankCreateModal(),
      editModal: blankEditModal(),
      consumptionModal: { show: false, items: [] }
    };
  },
  watch: {
    "program.id"() {
      this.fetchCards();
    }
  },
  created() {
    this.fetchCards();
  },
  methods: {
    formatDate(d) {
      if (!d) return "—";
      const raw = typeof d === "string" ? d : (d.String || "");
      if (!raw) return "—";
      const date = new Date(raw);
      if (isNaN(date.getTime())) return raw;
      return date.toLocaleString();
    },

    async fetchCards() {
      if (!this.program || !this.program.id) return;
      this.loading = true;
      try {
        const res = await axios.get(`/loyalty/program/${this.program.id}/cards`);
        this.cards = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.makeToast("Error", "No se pudieron cargar las tarjetas: " + this.errMsg(e), "danger");
      } finally {
        this.loading = false;
      }
    },

    openCreateModal() {
      this.createModal = blankCreateModal();
      this.createModal.show = true;
    },

    closeCreateModal() {
      this.createModal.show = false;
      this.createModal.createdCards = [];
    },

    async postCreateCards() {
      if (!this.createModal.quantity || this.createModal.quantity < 1) {
        this.makeToast("Error", "La cantidad debe ser al menos 1", "danger");
        return;
      }
      if (this.createModal.points < 0) {
        this.makeToast("Error", "Los puntos no pueden ser negativos", "danger");
        return;
      }
      const payload = {
        expiration_date: this.createModal.expiration_date || "",
        points: this.createModal.points || 0
      };
      if (this.createModal.quantity === 1 && this.createModal.code.trim()) {
        payload.code = this.createModal.code.trim();
      }
      this.createModal.submitting = true;
      try {
        const res = await axios.post(
          `/loyalty/program/${this.program.id}/card?qty=${this.createModal.quantity}`,
          payload
        );
        this.createModal.createdCards = Array.isArray(res.data) ? res.data : [];
        await this.fetchCards();
        this.makeToast("Éxito", `${this.createModal.createdCards.length} código(s) creado(s)`, "success");
      } catch (e) {
        this.makeToast("Error", "No se pudieron crear los códigos: " + this.errMsg(e), "danger");
      } finally {
        this.createModal.submitting = false;
      }
    },

    async editCard(id) {
      try {
        const res = await axios.get(`/loyalty/program/${this.program.id}/card/${id}`);
        this.editModal = blankEditModal();
        this.editModal.card = { ...res.data };
        this.editModal.show = true;
      } catch (e) {
        this.makeToast("Error", "No se pudo cargar la tarjeta: " + this.errMsg(e), "danger");
      }
    },

    async saveCardData() {
      this.editModal.savingData = true;
      try {
        const res = await axios.put(
          `/loyalty/program/${this.program.id}/card/${this.editModal.card.id}`,
          this.editModal.card
        );
        this.editModal.card = { ...res.data };
        await this.fetchCards();
        this.makeToast("Éxito", "Datos de la tarjeta actualizados", "success");
      } catch (e) {
        this.makeToast("Error", "No se pudo guardar: " + this.errMsg(e), "danger");
      } finally {
        this.editModal.savingData = false;
      }
    },

    async addPointsServer() {
      const n = this.editModal.addPoints;
      if (!n || n <= 0) return;
      this.editModal.movingPoints = true;
      try {
        await axios.get(`/loyalty/program/${this.program.id}/card/${this.editModal.card.id}/addPoints/${n}`);
        this.editModal.card.points += n;
        this.editModal.addPoints = null;
        await this.fetchCards();
        this.makeToast("Éxito", `Se agregaron ${n} punto(s)`, "success");
      } catch (e) {
        this.makeToast("Error", "No se pudieron sumar puntos: " + this.errMsg(e), "danger");
      } finally {
        this.editModal.movingPoints = false;
      }
    },

    async subtractPointsServer() {
      const n = this.editModal.subtractPoints;
      if (!n || n <= 0) return;
      if (n > this.editModal.card.points) {
        this.makeToast("Error", "No puedes restar más puntos de los disponibles", "danger");
        return;
      }
      this.editModal.movingPoints = true;
      try {
        await axios.get(`/loyalty/program/${this.program.id}/card/${this.editModal.card.id}/subtractPoints/${n}`);
        this.editModal.card.points -= n;
        this.editModal.subtractPoints = null;
        await this.fetchCards();
        this.makeToast("Éxito", `Se restaron ${n} punto(s)`, "success");
      } catch (e) {
        this.makeToast("Error", "No se pudieron restar puntos: " + this.errMsg(e), "danger");
      } finally {
        this.editModal.movingPoints = false;
      }
    },

    async viewConsumption(code) {
      try {
        const res = await axios.get(`/loyalty/card/${code}/use`);
        this.consumptionModal.items = Array.isArray(res.data) ? res.data : [];
        this.consumptionModal.show = true;
      } catch (e) {
        this.makeToast("Error", "No se pudo cargar el historial: " + this.errMsg(e), "danger");
      }
    },

    errMsg(e) {
      return (e && e.response && (e.response.data || e.response.statusText)) || e.message || "error";
    },
    makeToast(title, content, variant = "default") {
      this.$bvToast.toast(content, { title, variant, solid: true, autoHideDelay: 5000 });
    }
  }
};
</script>

<style scoped>
.fa-history,
.fa-plus,
.fa-minus,
.fa-download {
  margin-right: 0;
}
</style>
